import type { TRPCLink } from "@trpc/client";
import { TRPCClientError, httpBatchLink as httpBatchLinkFactory, loggerLink } from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { observable } from "@trpc/server/observable";
import { log } from "next-axiom";
import superjson from "superjson";

import { toast } from "@sorcery/ui";

import { env } from "~/env";
import type { AppRouter } from "~/server/trpc/root";
import { loginPath } from "./paths";

/**
 * Inference helper for inputs
 * @example type HelloInput = RouterInputs['example']['hello']
 **/
export type RouterInputs = inferRouterInputs<AppRouter>;
/**
 * Inference helper for outputs
 * @example type HelloOutput = RouterOutputs['example']['hello']
 **/
export type RouterOutputs = inferRouterOutputs<AppRouter>;

const sessionExpiryLink: TRPCLink<AppRouter> = () => {
  // here we just got initialized in the app - this happens once per app
  // useful for storing cache for instance
  return ({ next, op }) => {
    // this is when passing the result to the next link
    // each link needs to return an observable which propagates results
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          // we received value from previous link
          observer.next(value);
        },
        error(err) {
          // we received error from previous link
          observer.error(err);
          if (err?.data?.code === "UNAUTHORIZED") {
            // biome-ignore lint/style/noRestrictedGlobals: <explanation>
            const win: Window | undefined = window;
            /**
             * Axiom trigger. Do not change this logged string
             */
            log.info("⚡ Sorcery 401 | session expired, redirecting to logout", {
              error: err,
              message: err.message ?? "No error message",
              path: err.data.path ?? "No path",

              user: (win as unknown as { axiomUser: string })?.axiomUser ?? "anonymous",
            });

            if (win) {
              win.location = loginPath(win.location.pathname + win.location.search);
            }
          }
        },
        complete() {
          observer.complete();
        },
      });
      return unsubscribe;
    });
  };
};

const httpBatchLink = httpBatchLinkFactory({
  /**
   * If you want to use SSR, you need to use the server's full URL
   * @link https://trpc.io/docs/v11/ssr
   **/
  url: `${getBaseUrl()}/api/trpc`,
  transformer: superjson,
  // handle version skews via outgoing and incoming headers
  headers() {
    const headers = new Headers();
    headers.set("x-trpc-source", "nextjs-react");
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    headers.set("x-build-id", env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA!);
    return headers;
  },

  async fetch(url, options) {
    const response = await fetch(url, options);
    if (response.headers.get("x-version-skew")) {
      // save skew context against the global window object to check on frontend crash
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      if (typeof window !== "undefined") (window as any).skew = true;

      // alert if api error
      if (!response.ok) {
        /**
         * Axiom trigger. Do not change this logged string
         */
        log.info("⚡ Sorcery version skew | showing popup", {
          message: response.statusText ?? "No status text",
          path: response.url ?? "No path",
          // biome-ignore lint/style/noRestrictedGlobals: <explanation>
          user: (window as any)?.axiomUser ?? "anonymous",
        });
        toast.error("Sorcery has been updated, please reload the page.", {
          duration: Number.POSITIVE_INFINITY,
          dismissible: false,
          action: {
            label: "Reload",
            // biome-ignore lint/style/noRestrictedGlobals: <explanation>
            onClick: () => window.location.reload(),
          },
          closeButton: false,
        });
      }
    } else {
      // clear skew context from the global window object
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      if (typeof window !== "undefined") (window as any).skew = false;
    }

    if (response.headers.get("x-offline")) {
      /**
       * Axiom trigger. Do not change this logged string
       */
      log.info("⚡ Sorcery forced reload", {
        message: response.statusText ?? "No status text",
        path: response.url ?? "No path",
        // biome-ignore lint/style/noRestrictedGlobals: <explanation>
        user: (window as any)?.axiomUser ?? "anonymous",
      });
      //  will get picked up from the frontend handler to show offline splash screen
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      window?.location.reload();
    }
    return response;
  },
});

// skip retries on immutable failures. this reduces api hammering and by extension performance and rate limiting bottlenecks
// https://github.com/trpc/trpc/issues/4789
const retry = (failureCount: number, error: unknown) => {
  if (!(error instanceof TRPCClientError)) {
    return false;
  }

  const nonRetryableCodes = new Set([
    "BAD_REQUEST",
    "UNAUTHORIZED",
    "FORBIDDEN",
    "NOT_FOUND",
    "TOO_MANY_REQUESTS",
    "INTERNAL_SERVER_ERROR",
  ]);

  if (nonRetryableCodes.has((error.data as { code: string })?.code)) {
    return false;
  }

  return failureCount < 3;
};

export const api = createTRPCNext<AppRouter>({
  transformer: superjson,
  config() {
    return {
      queryClientConfig: {
        defaultOptions: {
          queries: {
            retry,
          },
        },
      },
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
        }),
        sessionExpiryLink,
        httpBatchLink,
      ],
    };
  },
  /**
   * @link https://trpc.io/docs/v11/ssr
   **/
  ssr: false,
});

function getBaseUrl() {
  // biome-ignore lint/style/noRestrictedGlobals: <explanation>
  if (typeof window !== "undefined") return ""; // browser should use relative url
  if (env.VERCEL_URL) return env.VERCEL_URL; // SSR should use vercel url
  return `http://localhost:${env.PORT}`; // dev SSR should use localhost
}
